import { algoliasearch, SearchClient } from "algoliasearch";
import { useState, useEffect, useContext } from "react";
import {
  InstantSearch,
  useInfiniteHits,
  useSearchBox,
} from "react-instantsearch";
import actions from "src/actions";
import { FansTable } from "./FansTable";
import {
  AlgoliaContactObject,
  AlgoliaIndexType,
  AlgoliaSortOrder,
} from "common/interfaces/Algolia";
import { Box, HStack, Icon, Spinner, Text, VStack } from "@chakra-ui/react";
import { FiSearch } from "react-icons/fi";
import KYDSearchInput from "src/ui/KYDSearchInput";
import { handleIndexNameTransform } from "src/utils";
import AddFanDialog from "./AddFanDialog";
import KYDMenuButton from "src/ui/KYDMenuButton";
import { useNavigate } from "react-router-dom";
import { useToast } from "@chakra-ui/react";
import DeleteContactDialog, {
  DeleteContactDialogContext,
  withDeleteContactDialog,
} from "./DeleteContactDialog";
import { KYDContact } from "@common/interfaces/KYDCampaign";
import { KYDScope } from "@common/interfaces/ICognito";
import ShowIfHasScope from "src/Manager/ShowIfHasScope";

const defaultState = {
  sortBy: "last_active_at",
  sortOrder: "descending" as AlgoliaSortOrder,
};

const Fans = ({ permissions = [] }: { permissions?: KYDScope[] }) => {
  const [algoliaConfig, setAlgoliaConfig] = useState<{
    indexName: string;
    searchClient: SearchClient;
  }>(null);

  useEffect(() => {
    const fetchFans = async () => {
      const response = await actions.ADMIN.fetchAlgoliaConfig({
        indexType: AlgoliaIndexType.CONTACTS,
      });
      if (response.events_config) {
        const search = algoliasearch(
          response.events_config.app_id,
          response.events_config.api_key
        );
        search.clearCache();
        setAlgoliaConfig({
          indexName: response.events_config.index_name,
          searchClient: search,
        });
      }
    };
    fetchFans();
  }, []);

  const onChangeSort = (sortBy: string, sortOrder: string) => {
    const newIndexName = handleIndexNameTransform(
      algoliaConfig.indexName,
      sortBy,
      sortOrder as AlgoliaSortOrder,
      defaultState
    );
    setAlgoliaConfig({
      ...algoliaConfig,
      indexName: newIndexName,
    });
  };

  if (!algoliaConfig?.indexName) {
    return <Spinner />;
  }
  return (
    <InstantSearch
      searchClient={algoliaConfig.searchClient}
      indexName={algoliaConfig.indexName}
    >
      <FansTableWrapper permissions={permissions} onChangeSort={onChangeSort} />
    </InstantSearch>
  );
};

const FansTableWrapper = ({
  onChangeSort,
  permissions,
}: {
  onChangeSort: (sortBy: string, sortOrder: string) => void;
  permissions: KYDScope[];
}) => {
  const [isAddFanDialogOpen, setIsAddFanDialogOpen] = useState(false);

  const [localContacts, setLocalContacts] = useState<AlgoliaContactObject[]>(
    []
  );
  const [deletedContactIds, setDeletedContactIds] = useState<string[]>([]);
  const toast = useToast();
  const { open: openDeleteContactDialog, close: closeDeleteContactDialog } =
    useContext(DeleteContactDialogContext);

  const {
    items: fansAlgolia,
    results: { nbHits },
  } = useInfiniteHits<AlgoliaContactObject>({
    escapeHTML: false,
    showPrevious: false,
  });
  const { refine, query } = useSearchBox();
  const navigate = useNavigate();

  // Merge Algolia and local contacts, prioritizing local changes and filtering out deleted contacts
  const fans = [
    ...localContacts,
    ...fansAlgolia.filter(
      (fan) =>
        !localContacts.some((local) => local.objectID === fan.objectID) &&
        !deletedContactIds.includes(fan.objectID)
    ),
  ];

  // Optimistic create
  const onCreate = (contact: KYDContact) => {
    const newContact = {
      ...contact,
      objectID: contact.id || `local-${Date.now()}`,
      last_active_at_unix: Math.floor(Date.now() / 1000),
    };
    setLocalContacts((prev) => {
      // Replace if exists, else add
      const filtered = prev.filter((c) => c.objectID !== newContact.objectID);
      return [newContact, ...filtered];
    });
    setDeletedContactIds((prev) =>
      prev.filter((id) => id !== newContact.objectID)
    );
  };

  const handleClickDelete = (contact: AlgoliaContactObject) => {
    openDeleteContactDialog({
      text: `Are you sure you want to delete ${contact.first_name || ""} ${
        contact.last_name || ""
      } (${contact.email || "No email"})?`,
      title: "Delete Contact",
      actions: [
        {
          text: "Delete",
          type: "danger",
          onClick: () => handleDelete(contact),
          buttonProps: {
            bg: "red.500",
            color: "white",
            w: "auto",
          },
        },
        {
          text: "Cancel",
          type: "secondary",
          onClick: closeDeleteContactDialog,
        },
      ],
    });
  };

  // Optimistic delete
  const handleDelete = async (contact: AlgoliaContactObject) => {
    const { id } = contact;
    setDeletedContactIds((prev) => [...prev, id]);
    setLocalContacts((prev) => prev.filter((c) => c.objectID !== id));
    closeDeleteContactDialog();
    try {
      await actions.ADMIN.deleteContact(id);
      toast({
        title: "Contact deleted",
        description: "Contact deleted successfully",
        status: "success",
      });
    } catch (error) {
      console.error("Failed to delete contact:", error);
      toast({
        title: "Failed to delete contact",
        description: "Please try again",
        status: "error",
      });
      setDeletedContactIds((prev) =>
        prev.filter((contactId) => contactId !== id)
      );
    }
  };

  return (
    <VStack w="100%" py={4}>
      <ShowIfHasScope
        permissions={permissions}
        checkFor={["crm/contacts:write", "crm/write"]}
      >
        <>
          <AddFanDialog
            onCreate={onCreate}
            isOpen={isAddFanDialogOpen}
            onClose={() => setIsAddFanDialogOpen(false)}
          />
          <DeleteContactDialog onClose={() => {}} />
        </>
      </ShowIfHasScope>
      <HStack
        pb={6}
        alignItems="center"
        justifyContent="space-between"
        w="100%"
      >
        <HStack alignItems="center" gap={2}>
          <Text
            color="#1A202C"
            fontFamily="Work Sans"
            fontSize="32px"
            fontStyle="normal"
            fontWeight={400}
            lineHeight="30px"
            letterSpacing="-0.96px"
          >
            Contacts
          </Text>
          <Text
            color="rgba(26, 32, 44, 0.50)"
            fontFamily="Work Sans"
            fontSize="32px"
            fontStyle="normal"
            fontWeight={400}
            lineHeight="30px"
            letterSpacing="-0.96px"
          >
            {nbHits}
          </Text>
        </HStack>
        <ShowIfHasScope
          permissions={permissions}
          checkFor={["crm/contacts:write", "crm/write"]}
        >
          <KYDMenuButton
            options={[
              {
                label: "Create New",
                onClick: () => {
                  setIsAddFanDialogOpen(true);
                },
              },
            ]}
            title="Add Contacts"
          />
        </ShowIfHasScope>
      </HStack>
      <HStack w="100%">
        <KYDSearchInput
          w={["100%", null, 350]}
          leftIcon={<Icon as={FiSearch} color={"#969696"} />}
          items={[]}
          placeholder="Search for contact by name or email"
          onSelect={() => {}}
          value={query}
          onChange={refine}
        />
      </HStack>
      <Box w="100%" overflowX="auto" mt={6}>
        <FansTable
          permissions={permissions}
          fans={fans}
          onChangeSort={onChangeSort}
          handleDelete={handleClickDelete}
        />
      </Box>
    </VStack>
  );
};

export default withDeleteContactDialog(Fans);
